
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import { UserMessage } from '../api/entities.js';
import { format, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  MessageSquare,
  Search,
  Plus,
  Send,
  Paperclip,
  User as UserIcon,
  BookOpen,
  FileText,
  MailOpen,
  Inbox,
  Star,
  Archive,
  Trash2,
  RefreshCw,
  X,
  Check,
  ChevronDown,
  MoreHorizontal,
  Filter
} from 'lucide-react';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Label } from "../components/ui/label.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { ScrollArea } from "../components/ui/scroll-area.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.jsx";

export default function MessagesPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [newMessage, setNewMessage] = useState({
    subject: '',
    content: '',
    receiver_id: '',
    attachments: []
  });
  const [showNewMessageDialog, setShowNewMessageDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentFolder, setCurrentFolder] = useState('inbox');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadMessages();
    }
  }, [currentUser, currentFolder]);

  const loadUserData = async () => {
    try {
      const userData = await User.me();
      setCurrentUser(userData);
      
      // Carregar lista de usuários para envio de mensagens
      const usersData = await User.list();
      setUsers(usersData.filter(user => user.id !== userData.id));
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  };

  const loadMessages = async () => {
    setIsLoading(true);
    try {
      let messagesData = [];
      
      if (currentFolder === 'inbox') {
        // Recebidas
        messagesData = await UserMessage.filter({
          receiver_id: currentUser.id
        });
      } else if (currentFolder === 'sent') {
        // Enviadas
        messagesData = await UserMessage.filter({
          sender_id: currentUser.id
        });
      }
      
      // Ordenar por data de criação (mais recentes primeiro)
      messagesData.sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
      
      setMessages(messagesData);
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
    setIsLoading(false);
  };

  const handleSendMessage = async () => {
    if (!newMessage.subject || !newMessage.content || !newMessage.receiver_id) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }
    
    try {
      await UserMessage.create({
        sender_id: currentUser.id,
        receiver_id: newMessage.receiver_id,
        subject: newMessage.subject,
        content: newMessage.content,
        read: false,
        attachments: newMessage.attachments
      });
      
      setShowNewMessageDialog(false);
      setNewMessage({
        subject: '',
        content: '',
        receiver_id: '',
        attachments: []
      });
      
      // Se estiver na pasta de enviados, atualizar a lista
      if (currentFolder === 'sent') {
        loadMessages();
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Ocorreu um erro ao enviar a mensagem. Por favor, tente novamente.');
    }
  };

  const handleReply = async () => {
    if (!replyContent || !selectedMessage) return;
    
    try {
      await UserMessage.create({
        sender_id: currentUser.id,
        receiver_id: selectedMessage.sender_id,
        subject: `Re: ${selectedMessage.subject}`,
        content: replyContent,
        read: false,
        related_ticket_id: selectedMessage.related_ticket_id,
        attachments: []
      });
      
      setReplyContent('');
      loadMessages();
    } catch (error) {
      console.error('Erro ao responder mensagem:', error);
      alert('Ocorreu um erro ao responder a mensagem. Por favor, tente novamente.');
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await UserMessage.update(messageId, { read: true });
      
      setMessages(prev => 
        prev.map(msg => 
          msg.id === messageId ? { ...msg, read: true } : msg
        )
      );
    } catch (error) {
      console.error('Erro ao marcar mensagem como lida:', error);
    }
  };

  const deleteMessage = async (messageId) => {
    if (confirm('Tem certeza que deseja excluir esta mensagem?')) {
      try {
        await UserMessage.delete(messageId);
        
        if (selectedMessage && selectedMessage.id === messageId) {
          setSelectedMessage(null);
        }
        
        loadMessages();
      } catch (error) {
        console.error('Erro ao excluir mensagem:', error);
        alert('Ocorreu um erro ao excluir a mensagem. Por favor, tente novamente.');
      }
    }
  };

  const handleSelectMessage = (message) => {
    setSelectedMessage(message);
    
    // Se a mensagem ainda não foi lida, marcá-la como lida
    if (message.receiver_id === currentUser.id && !message.read) {
      markAsRead(message.id);
    }
  };

  const getSenderOrReceiverInfo = (message) => {
    const isInbox = message.receiver_id === currentUser.id;
    const userId = isInbox ? message.sender_id : message.receiver_id;
    
    const user = users.find(u => u.id === userId);
    
    return {
      name: user ? user.full_name : 'Usuário Desconhecido',
      email: user ? user.email : '',
      initials: user ? user.full_name.split(' ').map(n => n[0]).join('').toUpperCase() : '?'
    };
  };

  const filteredMessages = messages.filter(message => {
    if (!searchQuery) return true;
    
    const search = searchQuery.toLowerCase();
    const userInfo = getSenderOrReceiverInfo(message);
    
    return (
      message.subject?.toLowerCase().includes(search) ||
      message.content?.toLowerCase().includes(search) ||
      userInfo.name.toLowerCase().includes(search) ||
      userInfo.email.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Mensagens</h1>
          <p className="text-muted-foreground">Gerencie suas comunicações com a equipe</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={loadMessages}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          
          <Dialog open={showNewMessageDialog} onOpenChange={setShowNewMessageDialog}>
            <DialogTrigger asChild>
              <Button className="bg-primary">
                <Plus className="h-4 w-4 mr-2" />
                Nova Mensagem
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Nova Mensagem</DialogTitle>
                <DialogDescription>
                  Envie uma mensagem para outro usuário do sistema
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div className="flex items-center gap-4">
                  <Label htmlFor="recipient" className="min-w-[80px]">Para:</Label>
                  <select
                    value={newMessage.receiver_id}
                    onChange={(e) => setNewMessage({...newMessage, receiver_id: e.target.value})}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Selecione o destinatário</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id}>{user.full_name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-center gap-4">
                  <Label htmlFor="subject" className="min-w-[80px]">Assunto:</Label>
                  <Input
                    id="subject"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage({...newMessage, subject: e.target.value})}
                    placeholder="Assunto da mensagem"
                  />
                </div>
                
                <div>
                  <Label htmlFor="content">Mensagem:</Label>
                  <Textarea
                    id="content"
                    value={newMessage.content}
                    onChange={(e) => setNewMessage({...newMessage, content: e.target.value})}
                    placeholder="Digite sua mensagem aqui"
                    className="min-h-[200px] mt-2"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNewMessageDialog(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleSendMessage}>
                  <Send className="h-4 w-4 mr-2" />
                  Enviar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="md:col-span-1">
          <Card>
            <CardContent className="p-4">
              <div className="space-y-1">
                <Button
                  variant={currentFolder === 'inbox' ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setCurrentFolder('inbox')}
                >
                  <Inbox className="h-4 w-4 mr-2" />
                  Caixa de Entrada
                  <Badge className="ml-auto">{messages.filter(m => m.receiver_id === currentUser?.id && !m.read).length}</Badge>
                </Button>
                
                <Button
                  variant={currentFolder === 'sent' ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setCurrentFolder('sent')}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Enviadas
                </Button>
                
                <Button
                  variant={currentFolder === 'drafts' ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setCurrentFolder('drafts')}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Rascunhos
                </Button>
                
                <Button
                  variant={currentFolder === 'starred' ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setCurrentFolder('starred')}
                >
                  <Star className="h-4 w-4 mr-2" />
                  Favoritas
                </Button>
                
                <Button
                  variant={currentFolder === 'archived' ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setCurrentFolder('archived')}
                >
                  <Archive className="h-4 w-4 mr-2" />
                  Arquivadas
                </Button>
              </div>
              
              <Separator className="my-4" />
              
              <div className="space-y-4">
                <div className="text-sm font-medium">Filtros</div>
                
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input 
                    placeholder="Buscar mensagens"
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Lista de Mensagens e Detalhes */}
        <div className="md:col-span-3">
          <Card className="h-[calc(100vh-240px)]">
            <div className="h-full grid grid-cols-1 md:grid-cols-3">
              {/* Lista de Mensagens */}
              <div className="border-r">
                <div className="p-4 border-b">
                  <h3 className="font-medium">
                    {currentFolder === 'inbox' ? 'Caixa de Entrada' : 
                     currentFolder === 'sent' ? 'Enviadas' :
                     currentFolder === 'drafts' ? 'Rascunhos' :
                     currentFolder === 'starred' ? 'Favoritas' : 'Arquivadas'}
                  </h3>
                </div>
                
                <ScrollArea className="h-[calc(100vh-300px)]">
                  {isLoading ? (
                    <div className="flex items-center justify-center h-40">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                  ) : filteredMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground/40" />
                      <p className="mt-2 text-muted-foreground">
                        Nenhuma mensagem encontrada
                      </p>
                    </div>
                  ) : (
                    <div>
                      {filteredMessages.map((message) => {
                        const userInfo = getSenderOrReceiverInfo(message);
                        const isUnread = message.receiver_id === currentUser?.id && !message.read;
                        const isSelected = selectedMessage?.id === message.id;
                        
                        return (
                          <div
                            key={message.id}
                            className={`p-4 border-b cursor-pointer hover:bg-muted/40 transition-colors ${
                              isSelected ? 'bg-primary/5' : ''
                            } ${
                              isUnread ? 'bg-blue-50' : ''
                            }`}
                            onClick={() => handleSelectMessage(message)}
                          >
                            <div className="flex items-start gap-3">
                              <Avatar>
                                <AvatarFallback className="bg-primary/10 text-primary">
                                  {userInfo.initials}
                                </AvatarFallback>
                              </Avatar>
                              
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <div className={`font-medium truncate ${isUnread ? 'font-bold' : ''}`}>
                                    {userInfo.name}
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {formatDistanceToNow(new Date(message.created_date), { 
                                      addSuffix: true,
                                      locale: ptBR 
                                    })}
                                  </div>
                                </div>
                                
                                <div className={`text-sm truncate ${isUnread ? 'font-semibold' : ''}`}>
                                  {message.subject}
                                </div>
                                
                                <div className="text-xs text-muted-foreground truncate mt-1">
                                  {message.content}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>
              </div>
              
              {/* Detalhes da Mensagem */}
              <div className="md:col-span-2">
                {selectedMessage ? (
                  <div className="h-full flex flex-col">
                    <div className="p-4 border-b flex items-center justify-between">
                      <h3 className="font-medium truncate">
                        {selectedMessage.subject}
                      </h3>
                      
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setSelectedMessage(null)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => deleteMessage(selectedMessage.id)}>
                              <Trash2 className="h-4 w-4 mr-2" />
                              Excluir
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Star className="h-4 w-4 mr-2" />
                              Marcar como favorita
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Archive className="h-4 w-4 mr-2" />
                              Arquivar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                    
                    <ScrollArea className="flex-1">
                      <div className="p-4">
                        <div className="flex items-start gap-4">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getSenderOrReceiverInfo(selectedMessage).initials}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium">
                                  {getSenderOrReceiverInfo(selectedMessage).name}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {getSenderOrReceiverInfo(selectedMessage).email}
                                </div>
                              </div>
                              
                              <div className="text-xs text-muted-foreground">
                                {format(new Date(selectedMessage.created_date), 'PPp', { locale: ptBR })}
                              </div>
                            </div>
                            
                            {selectedMessage.related_ticket_id && (
                              <div className="mt-2 bg-blue-50 p-2 rounded-md text-sm">
                                <div className="font-medium">Chamado relacionado:</div>
                                <Button 
                                  variant="link" 
                                  className="p-0 h-auto text-sm"
                                  onClick={() => navigate(createPageUrl(`ViewTicket?id=${selectedMessage.related_ticket_id}`))}
                                >
                                  #{selectedMessage.related_ticket_id.substring(0, 8)}
                                </Button>
                              </div>
                            )}
                            
                            <div className="mt-4 text-sm whitespace-pre-wrap">
                              {selectedMessage.content}
                            </div>
                            
                            {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                              <div className="mt-4">
                                <div className="font-medium text-sm">Anexos:</div>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {selectedMessage.attachments.map((attachment, i) => (
                                    <Badge 
                                      key={i} 
                                      variant="outline"
                                      className="flex items-center gap-1"
                                    >
                                      <Paperclip className="h-3 w-3" />
                                      {attachment.name}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </ScrollArea>
                    
                    <div className="p-4 border-t">
                      <div className="flex items-start gap-4">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary/10 text-primary">
                            {currentUser?.full_name?.split(' ').map(n => n[0]).join('').toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1">
                          <Textarea
                            placeholder="Escreva sua resposta..."
                            value={replyContent}
                            onChange={(e) => setReplyContent(e.target.value)}
                            className="min-h-[100px]"
                          />
                          
                          <div className="flex justify-between items-center mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                            >
                              <Paperclip className="h-4 w-4 mr-2" />
                              Anexar
                            </Button>
                            
                            <Button
                              size="sm"
                              onClick={handleReply}
                              disabled={!replyContent}
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Responder
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-full flex flex-col items-center justify-center text-center p-8">
                    <MessageSquare className="h-16 w-16 text-muted-foreground/30" />
                    <h3 className="mt-4 text-lg font-medium">Selecione uma mensagem para visualizar</h3>
                    <p className="text-muted-foreground mt-1">
                      Ou crie uma nova usando o botão "Nova Mensagem"
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
